import { Box, Button, Flex, Heading, Spacer } from '@chakra-ui/react'
import React, { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { TooltipCell } from '@/components/Table/components/Cell/ToolTipCell'
import ApplicationTypeDetailModal from '../components/ApplicationTypeDetailModal'

//Constant
import { SystemVariableType } from '@/features/systemVariables/constants'

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import { useApplicationType } from '../useApplicationType'
import { useDispatch } from 'react-redux'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'

const ApplicationTypeListPage = () => {
  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:applicationType`, {})
  const query = convertTableStateToQuery(storedTableState)
  const [modalDetail, setModalDetail] = useState({
    show: false,
    isCreate: true,
  })

  const dispatch = useDispatch()
  const { useFetchAllApplicationTypes, useUpdateApplicationType, useRemoveApplicationType } = useApplicationType()
  const { useSystemVariableOptions } = useSystemVariable()
  const { data: applicationTypeResponse } = useFetchAllApplicationTypes(query)

  const updateApplicationType = useUpdateApplicationType()
  const removeApplicationType = useRemoveApplicationType()
  const columnHelper = createColumnHelper()
  const { data: applicationCategoryOptions } = useSystemVariableOptions(SystemVariableType.APPLICATION_CATEGORY, {
    status: 'active',
  })

  const handleCreateApplicationType = () => {
    setModalDetail({
      show: true,
      isCreate: true,
      detail: {},
    })
  }

  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateApplicationType.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch]
  )

  const handleDelete = useCallback(
    (id) => {
      if (!id) {
        toast.error('Missing Id to remove Category')
      }
      removeApplicationType.mutate(id, {
        onSuccess: () => {},
      })
    },
    [removeApplicationType]
  )

  const handleOnRowClick = (row) => {
    setModalDetail({
      show: true,
      isCreate: false,
      detail: row?.original,
    })
  }

  const defaultColumns = useMemo(
    () => [
      columnHelper.display({
        id: 'action',
        size: 100,
        enableHiding: false,
        enableColumnFilter: false,
        cell: ({ row: { original } }) => {
          return (
            <>
              {original?.status === 'active' && (
                <Flex _hover={{ cursor: 'pointer' }} color="gray.400" justifyContent={'center'} w="100%">
                  <MdDeleteOutline
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(original._id)
                    }}
                  />
                </Flex>
              )}
              {original?.status === 'inactive' && (
                <Flex _hover={{ cursor: 'pointer' }} color="green.400" justifyContent={'center'} w="100%">
                  <MdOutlineCheckCircle
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleActivate(original._id)
                    }}
                  />
                </Flex>
              )}
            </>
          )
        },
      }),

      columnHelper.accessor('category', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: applicationCategoryOptions,
        },
      }),
      columnHelper.accessor('name'),
      columnHelper.accessor('validity'),
      columnHelper.accessor('firstApproverName', {
        cell: ({ row: { original }, getValue }) => {
          return (
            <TooltipCell
              text={original?.firstApproverType === 'lineManager' ? 'Line Manager' : getValue()}
            ></TooltipCell>
          )
        },
      }),
      columnHelper.accessor('secondApproverName', {}),
      columnHelper.accessor('finalApproverName', {}),

      columnHelper.accessor('status', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: [
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ],
        },
      }),
    ],
    [applicationCategoryOptions, columnHelper, handleActivate, handleDelete]
  )

  return (
    <>
      <ApplicationTypeDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading> Application Type </Heading>
          <Spacer />

          <Button colorScheme="blue" onClick={handleCreateApplicationType}>
            Create Type
          </Button>
        </Flex>
        <Box p={5} overflowX="scroll">
          <FullTable
            data={applicationTypeResponse?.data ? applicationTypeResponse.data : []}
            rowCount={applicationTypeResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default ApplicationTypeListPage
